import React from "react";
import { Dialog, IconButton, Theme } from "@mui/material";
import { styled } from "@mui/styles";
import HelpIcon from "@mui/icons-material/Help";
import CloseIcon from "@mui/icons-material/Close";
import useDeviceSizes from "../portfolio/hooks/useDeviceSizes";
import useIsUkUserCountry from "src/utils/hooks/useIsUkUserCountry";

const DEFAULT_BUTTONS = [
  {
    label: "Navigate Our Portal",
    link: "https://support.calebandbrown.com/en/articles/5826680-navigating-the-portal",
  },
  {
    label: "How to Deposit",
    link: "https://support.calebandbrown.com/en/articles/3129827-how-do-i-deposit-fiat",
  },
  {
    label: "How to Trade",
    link: "https://support.calebandbrown.com/en/articles/6575819-how-do-i-trade-with-caleb-brown",
  },
  {
    label: "Refer a friend",
    link: "https://support.calebandbrown.com/en/articles/9923345-refer-a-friend-how-to-generate-your-code",
  },
];

const PendingVerificationModal = ({
  open = true,
  onClose = () => {},
  title = "Application in review",
  heading = "Check back soon!",
  message = "Your application is pending verification from our team. You will receive an email confirming your account verification once this is done.",
  timeframeTitle = "When can I expect to hear back?",
  timeframeMessage = "An individual application can take up to 1-2 business day, while an entity application can take up to 3 business days. Longer processing times may occur depending on the complexity and circumstances of the application.",
  tipsTitle = "Learn more about our Portal",
  tipsMessage = "Here are some tips that will be useful for you in navigating our Portal",
  buttons = DEFAULT_BUTTONS,
}) => {
  const { isMobile } = useDeviceSizes();
  const isUkUser = useIsUkUserCountry();
  const visibleButtons = buttons.filter(
    (button) => !(isUkUser && button.label.toLowerCase().includes("refer a friend")),
  );
  if (isMobile) {
    title = "Caleb & Brown";
  }

  return (
    <StyledDialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <StyledTitleContainer>
        <div />
        <StyledTitle>{title}</StyledTitle>
        <IconButton onClick={onClose} edge="end" aria-label="close" sx={{ color: "white" }}>
          <CloseIcon sx={{ fontSize: "32px" }} />
        </IconButton>
      </StyledTitleContainer>

      <StyledContentContainer>
        {isMobile && (
          <StyledHeading style={{ fontSize: "18px" }}>Application in review</StyledHeading>
        )}
        <StyledInfoSection backgroundcolor="#FFF" style={{ padding: "0px" }}>
          <StyledHeading>{heading}</StyledHeading>
          <StyledInfoText>{message}</StyledInfoText>
        </StyledInfoSection>

        <StyledInfoSection backgroundcolor="#F5F3EF">
          <StyledInfoTitle>
            <StyledInfoHeading>{timeframeTitle}</StyledInfoHeading>
            <HelpIcon sx={{ fontSize: "16px" }} />
          </StyledInfoTitle>
          <StyledInfoText>{timeframeMessage}</StyledInfoText>
        </StyledInfoSection>

        <StyledInfoSection backgroundcolor="#F8F9F9">
          <StyledInfoTitle>
            <StyledInfoHeading>{tipsTitle}</StyledInfoHeading>
            <HelpIcon sx={{ fontSize: "16px" }} />
          </StyledInfoTitle>
          <StyledInfoText>{tipsMessage}</StyledInfoText>

          <StyledButtonsContainer>
            {visibleButtons.map((button, index) => (
              <StyledOutlinedButton
                key={index}
                target="_blank"
                href={button.link}
                rel="noopener noreferrer">
                {button.label}
              </StyledOutlinedButton>
            ))}
          </StyledButtonsContainer>
        </StyledInfoSection>
      </StyledContentContainer>
    </StyledDialog>
  );
};

// Styled components
export const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    display: "flex",
    width: "465px",
    flexDirection: "column",
    alignItems: "center",
    borderRadius: "10px",
    backgroundColor: theme.palette.background.paper,
    overflow: "hidden",
    [theme.breakpoints.down("md")]: {
      width: "100%",
      height: "100%",
      maxHeight: "100%",
      maxWidth: "100%",
      margin: 0,
      borderRadius: 0,
    },
  },
}));

export const StyledTitleContainer = styled("div")(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: "#fff",
  width: "100%",
  borderRadius: "10px 10px 0px 0px",
  display: "flex",
  padding: "12px 24px",
  alignItems: "center",
  justifyContent: "space-between",

  [theme.breakpoints.down("md")]: {
    flexDirection: "row-reverse",
    borderRadius: "0px",
  },
}));

export const StyledTitle = styled("div")(({ theme }) => ({
  fontWeight: 600,
  fontSize: "24px",
}));

export const StyledContentContainer = styled("div")(({ theme }) => ({
  padding: "24px",
  width: "100%",
  display: "flex",
  flexDirection: "column",
  gap: "24px",
}));

export const StyledHeading = styled("div")(({ theme }) => ({
  color: theme.palette.text.primary,
  fontSize: "16px",
  fontWeight: 600,
  fontStyle: "normal",
}));

interface InfoSectionProps {
  theme: Theme;
  backgroundcolor?: string;
}
export const StyledInfoSection = styled("div")(({ theme, backgroundcolor }: InfoSectionProps) => ({
  display: "flex",
  flexDirection: "column",
  backgroundColor: backgroundcolor || "#FFF",
  borderRadius: "8px",
  width: "100%",
  gap: "8px",
  padding: "12px",
}));

export const StyledInfoTitle = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: "8px",
}));

export const StyledInfoHeading = styled("div")(({ theme }) => ({
  color: theme.palette.text.primary,
  fontSize: "14px",
  fontWeight: 600,
  fontStyle: "normal",
}));

export const StyledInfoText = styled("p")(({ theme }) => ({
  color: theme.palette.text.primary,
  fontSize: "14px",
  fontStyle: "normal",
  fontWeight: 400,
  margin: 0,
}));

export const StyledButtonsContainer = styled("div")(({ theme }) => ({
  display: "flex",
  flexWrap: "wrap",
  gap: "12px",

  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    gap: "16px",
  },
}));

export const StyledOutlinedButton = styled("a")(({ theme }) => ({
  borderRadius: "20px",
  padding: "4px 8px",
  textTransform: "none",
  border: `1px solid ${theme.palette.primary.main}`,
  color: theme.palette.primary.main,
  backgroundColor: "#F8F9F9",
  textDecoration: "none", // Remove underline from links
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  //   width: "auto",
  cursor: "pointer",

  "&:hover": {
    backgroundColor: theme.palette.primary.main,
    color: "#fff",
    textDecoration: "none", // Ensure no underline on hover
  },
}));

export default PendingVerificationModal;
